import React, { useEffect, useState, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { User, Trash2, Plus, CheckCircle, AlertCircle, ChevronDown, Edit2 } from "lucide-react";
import { countries } from "../components/CountryName";

// Create fresh copy of empty child
const createFreshChild = (createEmptyChild) => {
    const newChild = createEmptyChild();
    return { ...newChild };
};

const ChildForm = ({ childs, setChilds, createEmptyChild, updateChild, loading, mode, preview, setPreview, onSave }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    // Ensure childs is always an array
    const safeChilds = Array.isArray(childs) ? childs : [];

    // Initialize expanded state for child cards
    useEffect(() => {
        const initialExpandedState = {};
        safeChilds.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeChilds.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addChild = useCallback(() => {
        if (setChilds && createEmptyChild) {
            setChilds(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newChild = createFreshChild(createEmptyChild);
                const newArray = [...currentArray, newChild];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setChilds, createEmptyChild]);

    const removeChild = useCallback((index) => {
        if (setChilds) {
            setChilds(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setChilds]);

    const handleUpdateChild = useCallback((index, field, value) => {
        if (updateChild) {
            updateChild(index, field, value);
        }
    }, [updateChild]);

    const validateForm = () => {
        const newErrors = {};
        safeChilds.forEach((child, i) => {
            if (!child.firstName) newErrors[`firstName_${i}`] = "First name is required";
            if (!child.lastName) newErrors[`lastName_${i}`] = "Last name is required";
            if (!child.dob) newErrors[`dob_${i}`] = "Date of birth is required";
            if (!child.gender) newErrors[`gender_${i}`] = "Gender is required";
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in view mode with preview
    if ((mode === "view" || preview) && !isEditing) {
        return (
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <User size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Children Details</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Information for visa processing and family composition</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Children information verified and approved</p>
                        </div>
                    )}

                    <div className="space-y-4 sm:space-y-5">
                        {safeChilds.filter(c => c.firstName).length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <User size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No children records added</p>
                            </div>
                        ) : (
                            safeChilds.filter(c => c.firstName).map((c, i) => (
                                <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">{c.firstName} {c.lastName}</h3>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                                        <Preview label="First Name" value={c.firstName} />
                                        <Preview label="Last Name" value={c.lastName} />
                                        <Preview label="Gender" value={c.gender} capitalize />
                                        <Preview label="Date of Birth" value={c.dob} isDate />
                                        <Preview label="Education" value={c.education} />
                                        <Preview label="Current Country" value={c.currentCountry} />
                                        <Preview label="Accompanying" value={c.isAccompanying ? "Yes" : "No"} highlight={c.isAccompanying} />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Children Information
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving children information...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <User size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Children Details</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Add information about your children</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {safeChilds.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <User size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No children records added</p>
                            <button
                                type="button"
                                onClick={addChild}
                                className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1 mx-auto"
                            >
                                <Plus size={16} /> Add your first child
                            </button>
                        </div>
                    ) : (
                        safeChilds.map((c, i) => (
                            <div key={i} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                {/* Card Header - Clickable on mobile */}
                                <div
                                    className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                                    onClick={() => toggleCard(i)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                {c.firstName || c.lastName ? `${c.firstName || ""} ${c.lastName || ""}`.trim() || "New Child" : "New Child"}
                                            </h3>
                                            {c.dob && (
                                                <p className="text-[10px] sm:text-xs text-gray-500">
                                                    DOB: {new Date(c.dob).toLocaleDateString()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            className="md:hidden text-gray-400"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCard(i);
                                            }}
                                        >
                                            <ChevronDown size={18} className={`transition-transform ${expandedCards[i] ? 'rotate-180' : ''}`} />
                                        </button>
                                        {safeChilds.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeChild(i);
                                                }}
                                                className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className={`
                                    transition-all duration-300 overflow-hidden
                                    ${expandedCards[i] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                `}>
                                    <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                            <Input
                                                label="First Name"
                                                value={c.firstName}
                                                onChange={(v) => handleUpdateChild(i, "firstName", v)}
                                                error={localErrors[`firstName_${i}`]}
                                                required
                                            />
                                            <Input
                                                label="Last Name"
                                                value={c.lastName}
                                                onChange={(v) => handleUpdateChild(i, "lastName", v)}
                                                error={localErrors[`lastName_${i}`]}
                                                required
                                            />

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
                                                    Date of Birth <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={c.dob ? new Date(c.dob).toISOString().split('T')[0] : ''}
                                                    onChange={(e) => handleUpdateChild(i, "dob", e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    min="1920-01-01"
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`dob_${i}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`dob_${i}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`dob_${i}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
                                                    Gender <span className="text-rose-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        value={c.gender}
                                                        onChange={(e) => handleUpdateChild(i, "gender", e.target.value)}
                                                        className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm
                                                            ${localErrors[`gender_${i}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                    >
                                                        <option value="">Select Gender</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                </div>
                                                {localErrors[`gender_${i}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`gender_${i}`]}</p>
                                                )}
                                            </div>

                                            <Input
                                                label="Education (Highest)"
                                                value={c.education}
                                                onChange={(v) => handleUpdateChild(i, "education", v)}
                                                placeholder="e.g., High School, Bachelor's"
                                            />

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
                                                    Current Country
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        value={c.currentCountry || ""}
                                                        onChange={(e) => handleUpdateChild(i, "currentCountry", e.target.value)}
                                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm"
                                                    >
                                                        <option value="">Select Country</option>
                                                        {countries.map((country) => (
                                                            <option key={country} value={country}>{country}</option>
                                                        ))}
                                                    </select>
                                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                </div>
                                            </div>

                                            <div className="sm:col-span-2">
                                                <label className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-blue-50/50 border border-blue-100 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={c.isAccompanying}
                                                        onChange={(e) => handleUpdateChild(i, "isAccompanying", e.target.checked)}
                                                    />
                                                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Accompanying to Canada?</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addChild}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 py-3 rounded-xl font-semibold text-sm transition-all"
                        >
                            <Plus size={18} /> Add Another Child
                        </button>


                    </div>
                </form>
            </div>
        </div>
    );
};

// Input Component with error handling
const Input = ({ label, value, onChange, required, placeholder, error }) => (
    <div>
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

// Preview Component
const Preview = ({ label, value, isDate, highlight, capitalize }) => (
    <div className="flex flex-col">
        <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{label}</span>
        <span className={`font-semibold text-gray-800 text-sm ${highlight ? 'text-blue-600' : ''} ${capitalize ? 'capitalize' : ''}`}>
            {isDate && value ? new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : (value || "—")}
        </span>
    </div>
);

export default ChildForm;